import { useState, useEffect, useRef, useId } from "react";
import { ArrowLeft, Send, Minimize2, Maximize2, Minus, X, MessageCircle } from "lucide-react";
import { toast as sonnerToast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useChat } from "@/contexts/ChatContext";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { serviceLabels } from "@/data/serviceLabels";

// -- Shared types & helpers --

interface ChatMessage {
  id: string;
  senderId: string;
  content: string;
  messageType: string;
  readAt: string | null;
  createdAt: string;
}

interface ConversationItem {
  id: string;
  bookingId: string;
  partnerId: string;
  partnerName: string;
  partnerAvatar: string | null;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  serviceType: string | null;
  startDate: string | null;
}

const formatTime = (dateStr: string) => {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 86400000) return d.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" });
  if (diff < 604800000) return d.toLocaleDateString("es", { weekday: "short", hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString("es", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

const formatRelativeTime = (dateStr: string): string => {
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return "ahora";
  if (diffMin < 60) return `hace ${diffMin} min`;
  if (diffHr < 24) return `hace ${diffHr}h`;
  if (diffDays === 1) return "ayer";
  if (diffDays < 7) return `hace ${diffDays} días`;
  return d.toLocaleDateString("es", { day: "numeric", month: "short" });
};

const playNotificationSound = () => {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 800;
    osc.type = "sine";
    gain.gain.value = 0.1;
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.stop(ctx.currentTime + 0.3);
  } catch {}
};

const PHONE_MASK_REGEX = /(\+503[\s-]?\d{4}[\s-]?\d{4}|\b\d{4}[\s-]?\d{4}\b|\b\d{7,8}\b)/g;
const EMAIL_MASK_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const URL_MASK_REGEX = /https?:\/\/[^\s]+/gi;
const ALERT_KEYWORDS_REGEX = /\b(whatsapp|telegram|mi número|mi numero|llamame|llámame|te paso mi|por fuera)\b/i;

const maskContactInfo = (text: string): { masked: string; wasMasked: boolean; flagged: boolean } => {
  let masked = text;
  let wasMasked = false;
  if (PHONE_MASK_REGEX.test(masked)) { masked = masked.replace(PHONE_MASK_REGEX, "***número oculto***"); wasMasked = true; }
  if (EMAIL_MASK_REGEX.test(masked)) { masked = masked.replace(EMAIL_MASK_REGEX, "***email oculto***"); wasMasked = true; }
  if (URL_MASK_REGEX.test(masked)) { masked = masked.replace(URL_MASK_REGEX, "***enlace oculto***"); wasMasked = true; }
  const flagged = ALERT_KEYWORDS_REGEX.test(text);
  return { masked, wasMasked, flagged };
};

// -- Main component --

const ChatPanel = () => {
  const { activeConversationId, isOpen, openChat, closeChat, minimizeChat } = useChat();
  const { profile } = useAuth();
  const listChannelId = useId();

  // Conversation view state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [convoLoading, setConvoLoading] = useState(true);
  const [partnerName, setPartnerName] = useState("");
  const [partnerAvatar, setPartnerAvatar] = useState<string | null>(null);
  const [partnerType, setPartnerType] = useState<"humano" | "compawdre">("humano");
  const [conversationStatus, setConversationStatus] = useState<string>("active");
  const [bookingLabel, setBookingLabel] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevConvoIdRef = useRef<string | null>(null);

  // List view state
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [listLoading, setListLoading] = useState(true);

  // -- List logic --

  const fetchConversations = async () => {
    if (!profile) return;
    if (conversations.length === 0) setListLoading(true);
    try {
      const { data: convos, error } = await supabase
        .from("conversations")
        .select("id, booking_id, humano_id, compawdre_id, status, updated_at")
        .or(`humano_id.eq.${profile.id},compawdre_id.eq.${profile.id}`)
        .order("updated_at", { ascending: false });

      if (error || !convos || convos.length === 0) {
        setConversations([]);
        setListLoading(false);
        return;
      }

      const enriched: ConversationItem[] = await Promise.all(
        convos.map(async (c) => {
          const partnerId = c.humano_id === profile.id ? c.compawdre_id : c.humano_id;

          const { data: partnerProfile } = await supabase
            .from("profiles")
            .select("full_name, avatar_url")
            .eq("id", partnerId)
            .single();

          const { data: lastMsgs } = await supabase
            .from("chat_messages")
            .select("content, created_at")
            .eq("conversation_id", c.id)
            .order("created_at", { ascending: false })
            .limit(1);

          const { count } = await supabase
            .from("chat_messages")
            .select("id", { count: "exact", head: true })
            .eq("conversation_id", c.id)
            .neq("sender_id", profile.id)
            .is("read_at", null);

          const { data: booking } = await supabase
            .from("bookings")
            .select("service_type, start_date")
            .eq("id", c.booking_id)
            .single();

          const lastMsg = lastMsgs?.[0];
          return {
            id: c.id,
            bookingId: c.booking_id,
            partnerId,
            partnerName: partnerProfile?.full_name || "Usuario",
            partnerAvatar: partnerProfile?.avatar_url || null,
            lastMessage: lastMsg?.content || "",
            lastMessageAt: lastMsg?.created_at || c.updated_at,
            unreadCount: count || 0,
            serviceType: booking?.service_type || null,
            startDate: booking?.start_date || null,
          };
        })
      );

      enriched.sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());
      setConversations(enriched);
    } catch (err) {
      console.error("Error fetching conversations:", err);
    } finally {
      setListLoading(false);
    }
  };

  // Fetch list when panel opens in list mode
  useEffect(() => {
    if (isOpen && !activeConversationId && profile) {
      fetchConversations();
    }
  }, [isOpen, activeConversationId, profile]);

  // Realtime for list refresh
  useEffect(() => {
    if (!profile || !isOpen || activeConversationId) return;

    const channel = supabase
      .channel(`chat-list-${listChannelId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "chat_messages" },
        () => fetchConversations()
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [profile, isOpen, activeConversationId]);

  // -- Conversation logic --

  useEffect(() => {
    if (!activeConversationId) {
      prevConvoIdRef.current = null;
      return;
    }

    // Only reset state when switching to a different conversation
    if (activeConversationId !== prevConvoIdRef.current) {
      setMessages([]);
      setPartnerName("");
      setPartnerAvatar(null);
      setConversationStatus("active");
      setUnauthorized(false);
      setNewMessage("");
      setBookingLabel(null);
      prevConvoIdRef.current = activeConversationId;
    }

    if (!profile) return;

    const init = async () => {
      if (messages.length === 0) setConvoLoading(true);

      const { data: convo, error: convoErr } = await supabase
        .from("conversations")
        .select("humano_id, compawdre_id, status, booking_id")
        .eq("id", activeConversationId)
        .single();

      if (convoErr || !convo) {
        setUnauthorized(true);
        setConvoLoading(false);
        return;
      }

      if (convo.humano_id !== profile.id && convo.compawdre_id !== profile.id) {
        setUnauthorized(true);
        setConvoLoading(false);
        return;
      }

      setConversationStatus(convo.status);

      const { data: bk } = await supabase
        .from("bookings")
        .select("service_type, start_date")
        .eq("id", convo.booking_id)
        .single();
      if (bk) {
        const label = serviceLabels[bk.service_type] || bk.service_type;
        const date = new Date(bk.start_date).toLocaleDateString("es", { day: "numeric", month: "short" });
        setBookingLabel(`${label} · ${date}`);
      }

      const partnerId = convo.humano_id === profile.id ? convo.compawdre_id : convo.humano_id;
      const isPartnerHumano = partnerId === convo.humano_id;

      const { data: p } = await supabase
        .from("profiles")
        .select("full_name, avatar_url, user_type")
        .eq("id", partnerId)
        .single();

      if (p) {
        setPartnerName(p.full_name);
        setPartnerAvatar(p.avatar_url);
        setPartnerType(p.user_type as "humano" | "compawdre");
      } else {
        setPartnerType(isPartnerHumano ? "humano" : "compawdre");
      }

      const { data: msgs } = await supabase
        .from("chat_messages")
        .select("id, sender_id, content, message_type, read_at, created_at")
        .eq("conversation_id", activeConversationId)
        .order("created_at", { ascending: true });

      setMessages((msgs || []).map((m) => ({
        id: m.id,
        senderId: m.sender_id,
        content: m.content,
        messageType: m.message_type,
        readAt: m.read_at,
        createdAt: m.created_at,
      })));

      const unreadIds = (msgs || [])
        .filter((m) => m.sender_id !== profile.id && !m.read_at)
        .map((m) => m.id);
      if (unreadIds.length > 0) {
        await supabase
          .from("chat_messages")
          .update({ read_at: new Date().toISOString() })
          .in("id", unreadIds);
      }

      setConvoLoading(false);
    };

    init();
  }, [profile, activeConversationId]);

  // Realtime for active conversation
  useEffect(() => {
    if (!profile || !activeConversationId) return;

    const channel = supabase
      .channel(`chat-${activeConversationId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "chat_messages", filter: `conversation_id=eq.${activeConversationId}` },
        async (payload) => {
          const msg = payload.new as any;
          const newMsg: ChatMessage = {
            id: msg.id,
            senderId: msg.sender_id,
            content: msg.content,
            messageType: msg.message_type,
            readAt: msg.read_at,
            createdAt: msg.created_at,
          };

          setMessages((prev) => {
            if (prev.some((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });

          if (msg.sender_id !== profile.id) {
            playNotificationSound();
            await supabase
              .from("chat_messages")
              .update({ read_at: new Date().toISOString() })
              .eq("id", msg.id);
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "chat_messages", filter: `conversation_id=eq.${activeConversationId}` },
        (payload) => {
          const updated = payload.new as any;
          setMessages((prev) =>
            prev.map((m) => m.id === updated.id ? { ...m, readAt: updated.read_at } : m)
          );
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [profile, activeConversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen && activeConversationId) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, activeConversationId]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !profile || !activeConversationId || sending) return;
    setSending(true);
    try {
      const { masked, wasMasked, flagged } = maskContactInfo(newMessage.trim());

      await supabase.from("chat_messages").insert({
        conversation_id: activeConversationId,
        sender_id: profile.id,
        content: masked,
        message_type: flagged ? "alert" : "user",
      });

      if (wasMasked || flagged) {
        sonnerToast("Por la seguridad de todos, la información de contacto se comparte a través de la plataforma.", {
          style: { backgroundColor: "#fdf8e3", color: "#252a2a" },
          duration: 5000,
        });
      }

      setNewMessage("");
      inputRef.current?.focus();
    } catch (err) {
      console.error("Error sending message:", err);
    } finally {
      setSending(false);
    }
  };

  const shouldShowTime = (idx: number) => {
    if (idx === messages.length - 1) return true;
    const curr = messages[idx];
    const next = messages[idx + 1];
    if (curr.senderId !== next.senderId) return true;
    if (curr.messageType !== next.messageType) return true;
    return new Date(next.createdAt).getTime() - new Date(curr.createdAt).getTime() > 300000;
  };

  // -- Render --

  if (!isOpen) return null;

  const isBlocked = conversationStatus === "blocked";
  const inListMode = !activeConversationId;

  return (
    <>
      {/* Mobile backdrop */}
      <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={closeChat} />

      {/* Panel */}
      <div
        className={cn(
          "fixed z-50 flex flex-col bg-white shadow-xl transition-all duration-200 ease-in-out",
          "inset-0",
          isExpanded
            ? "md:inset-4 md:w-auto md:h-auto md:rounded-2xl md:border md:border-gray-200"
            : "md:inset-auto md:bottom-4 md:right-4 md:w-[400px] md:h-[550px] md:rounded-2xl md:border md:border-gray-200"
        )}
      >
        {/* Header */}
        <div
          className="flex-shrink-0 px-4 flex items-center gap-3 md:rounded-t-2xl"
          style={{ backgroundColor: "#ffffff", borderBottom: "1px solid #e5e7eb", minHeight: "60px" }}
        >
          {/* Mobile back/close button */}
          <button
            onClick={() => inListMode ? closeChat() : openChat(null)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center md:hidden"
          >
            <ArrowLeft size={22} style={{ color: "#252a2a" }} />
          </button>

          {/* Desktop back button (conversation mode only) */}
          {!inListMode && (
            <button
              onClick={() => openChat(null)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors hidden md:flex items-center justify-center"
            >
              <ArrowLeft size={18} style={{ color: "#252a2a" }} />
            </button>
          )}

          {inListMode ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <MessageCircle size={20} style={{ color: "#252a2a" }} />
              <p className="font-fredoka text-lg" style={{ fontWeight: 700, color: "#252a2a" }}>
                Mensajes
              </p>
            </div>
          ) : (
            <>
              {partnerAvatar ? (
                <img src={partnerAvatar} alt={partnerName} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#d9eeff" }}>
                  <span className="font-fredoka text-sm" style={{ color: "#3273a4" }}>
                    {partnerName.charAt(0)?.toUpperCase() || "?"}
                  </span>
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-poppins truncate leading-tight" style={{ fontWeight: 600, color: "#252a2a", fontSize: "14px" }}>
                  {partnerName || "Cargando..."}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                  <span
                    className="inline-block font-poppins text-[10px] px-1.5 py-px rounded-full flex-shrink-0"
                    style={
                      partnerType === "compawdre"
                        ? { backgroundColor: "#eef4e9", color: "#93cfa6" }
                        : { backgroundColor: "#d9eeff", color: "#3273a4" }
                    }
                  >
                    {partnerType === "compawdre" ? "Compawdre" : "Humano"}
                  </span>
                  {bookingLabel && (
                    <span className="font-poppins text-[10px] truncate" style={{ color: "#9ca3af" }}>
                      {bookingLabel}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Desktop: expand, minimize & close */}
          <div className="hidden md:flex items-center gap-1">
            <button
              onClick={() => setIsExpanded((v) => !v)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title={isExpanded ? "Compactar" : "Expandir"}
            >
              {isExpanded ? <Minimize2 size={18} style={{ color: "#252a2a" }} /> : <Maximize2 size={18} style={{ color: "#252a2a" }} />}
            </button>
            <button
              onClick={() => { setIsExpanded(false); minimizeChat(); }}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Minimizar"
            >
              <Minus size={18} style={{ color: "#252a2a" }} />
            </button>
            <button onClick={closeChat} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" title="Cerrar">
              <X size={18} style={{ color: "#252a2a" }} />
            </button>
          </div>
        </div>

        {/* Body: list mode or conversation mode */}
        {inListMode ? (
          <div className="flex-1 overflow-y-auto">
            {listLoading ? (
              <div className="space-y-3 p-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-[72px] bg-gray-100 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center px-4">
                <MessageCircle size={48} style={{ color: "#d9eeff" }} />
                <p className="font-poppins mt-4" style={{ color: "#6b7280" }}>
                  No tenés conversaciones aún
                </p>
                <p className="font-poppins text-sm mt-1" style={{ color: "#9ca3af" }}>
                  Las conversaciones se crean cuando una reserva es aprobada
                </p>
              </div>
            ) : (
              conversations.map((convo, idx) => (
                <button
                  key={convo.id}
                  onClick={() => openChat(convo.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left min-h-[72px]"
                  style={{ borderBottom: idx < conversations.length - 1 ? "1px solid #f3f4f6" : "none" }}
                >
                  {convo.partnerAvatar ? (
                    <img src={convo.partnerAvatar} alt={convo.partnerName} className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#d9eeff" }}>
                      <span className="font-fredoka text-base" style={{ color: "#3273a4" }}>
                        {convo.partnerName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-poppins truncate" style={{ fontWeight: 600, color: "#252a2a" }}>
                        {convo.partnerName}
                      </span>
                      <span className="font-poppins text-xs flex-shrink-0" style={{ color: "#9ca3af" }}>
                        {formatRelativeTime(convo.lastMessageAt)}
                      </span>
                    </div>
                    {convo.serviceType && (
                      <p className="font-poppins text-[11px] truncate mt-0.5" style={{ color: "#3273a4" }}>
                        {serviceLabels[convo.serviceType] || convo.serviceType}
                        {convo.startDate && ` · ${new Date(convo.startDate).toLocaleDateString("es", { day: "numeric", month: "short" })}`}
                      </p>
                    )}
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <p className="font-poppins text-sm truncate" style={{ color: "#6b7280" }}>
                        {convo.lastMessage || "Sin mensajes aún"}
                      </p>
                      {convo.unreadCount > 0 && (
                        <span
                          className="flex-shrink-0 rounded-full flex items-center justify-center text-xs text-white min-w-[20px] h-5 px-1"
                          style={{ backgroundColor: "#c33c04", fontWeight: 700 }}
                        >
                          {convo.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        ) : (
          <>
            {/* Blocked banner */}
            {isBlocked && (
              <div className="flex-shrink-0 text-center py-2 px-4 font-poppins text-sm" style={{ backgroundColor: "#fde3d4", color: "#c33c04" }}>
                Esta conversación ha sido bloqueada por el equipo de Compaws
              </div>
            )}

            {unauthorized ? (
              <div className="flex-1 flex flex-col items-center justify-center px-4">
                <p className="font-poppins text-base mb-4" style={{ color: "#252a2a", fontWeight: 600 }}>
                  No tenés acceso a esta conversación
                </p>
                <button onClick={() => openChat(null)} className="font-poppins text-sm px-6 py-3 rounded-xl" style={{ backgroundColor: "#3273a4", color: "#ffffff" }}>
                  Volver
                </button>
              </div>
            ) : (
              <>
                {/* Messages area */}
                <div className="flex-1 overflow-y-auto px-4 py-2">
                  <p className="font-poppins text-center py-2 mb-2" style={{ fontSize: "11px", color: "#9ca3af" }}>
                    Esta conversación puede ser revisada por el equipo de Compaws para garantizar la seguridad del servicio.
                  </p>

                  {convoLoading ? (
                    <div className="flex items-center justify-center py-20">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center py-20">
                      <p className="font-poppins text-sm" style={{ color: "#9ca3af" }}>
                        Iniciá la conversación enviando un mensaje
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {messages.map((msg, idx) => {
                        const isMine = msg.senderId === profile?.id;
                        const isSystem = msg.messageType === "system" || msg.messageType === "alert";
                        const showTime = shouldShowTime(idx);

                        if (isSystem) {
                          return (
                            <div key={msg.id} className="flex justify-center py-2">
                              <div className="px-4 py-2 max-w-[80%] text-center" style={{ backgroundColor: "#fdf8e3", borderRadius: "12px" }}>
                                <p className="font-poppins text-sm italic" style={{ color: "#252a2a" }}>{msg.content}</p>
                                {showTime && (
                                  <p className="font-poppins mt-1" style={{ fontSize: "10px", color: "#9ca3af" }}>{formatTime(msg.createdAt)}</p>
                                )}
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div key={msg.id}>
                            <div className={cn("flex", isMine ? "justify-end" : "justify-start")}>
                              <div
                                className="max-w-[75%] px-4 py-2.5"
                                style={
                                  isMine
                                    ? { backgroundColor: "#3273a4", color: "#ffffff", borderRadius: "16px 16px 4px 16px" }
                                    : { backgroundColor: "#ffffff", color: "#252a2a", borderRadius: "16px 16px 16px 4px", border: "1px solid #e5e7eb" }
                                }
                              >
                                <p className="font-poppins text-sm whitespace-pre-wrap">{msg.content}</p>
                              </div>
                            </div>
                            {showTime && (
                              <div
                                className={cn("font-poppins mt-1 mb-2", isMine ? "text-right" : "text-left")}
                                style={{ paddingLeft: isMine ? 0 : 4, paddingRight: isMine ? 4 : 0 }}
                              >
                                <span style={{ fontSize: "10px", color: "#9ca3af" }}>{formatTime(msg.createdAt)}</span>
                                {isMine && (
                                  <span className="ml-1" style={{ fontSize: "12px", color: msg.readAt ? "#3273a4" : "#9ca3af" }}>
                                    {msg.readAt ? "\u2713\u2713" : "\u2713"}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input area */}
                {!isBlocked ? (
                  <div className="flex-shrink-0 px-4 py-3" style={{ backgroundColor: "#ffffff", borderTop: "1px solid #e5e7eb" }}>
                    <div className="flex items-center gap-2">
                      <input
                        ref={inputRef}
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                        placeholder="Escribe un mensaje..."
                        className="flex-1 font-poppins min-h-[44px] focus:outline-none focus:ring-2"
                        style={{ border: "1px solid #d1d5db", borderRadius: "24px", padding: "12px 16px", fontSize: "16px" }}
                      />
                      <button
                        onClick={sendMessage}
                        disabled={!newMessage.trim() || sending}
                        className="flex items-center justify-center disabled:opacity-40 transition-opacity"
                        style={{ backgroundColor: "#3273a4", color: "#ffffff", borderRadius: "50%", width: "44px", height: "44px", minWidth: "44px", minHeight: "44px" }}
                      >
                        <Send size={20} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex-shrink-0 px-4 py-4 text-center font-poppins text-sm" style={{ backgroundColor: "#ffffff", borderTop: "1px solid #e5e7eb", color: "#9ca3af" }}>
                    No podés enviar mensajes en esta conversación
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ChatPanel;
